import { OptionsApi } from '@core/swagger-client';
import { ExamWithPoints, QuestionType } from '@core/types';
import {
    Button,
    CompositePageHeader,
    ContainerNoTopSpace,
    ContainerWebinarInner,
    ContentHeader,
    Delete,
    Edit,
    ErrorMessagePage,
    Heading,
    Input,
    LoaderPage,
    PageHeader,
    PageHeaderContent,
    Plus,
    SortArrow,
    TabGroup,
    TextArea,
    WarningOutlined
} from '@core/ui';
import { useLocation, useNavigate, useParams } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditQuestion } from '../../organisms/EditQuestion';

interface ExamWithPointsEx extends ExamWithPoints {
    version: number;
}

export const EditExamPage = () => {
    const tabs = ['settings', 'intro', 'questions', 'outro', 'passed', 'failed'];
    const { webinarId } = useParams({ strict: false });
    const { hash } = useLocation();
    let activeTab = tabs.findIndex((t) => t === hash);
    if (activeTab === -1) {
        activeTab = 0;
    }

    const { t } = useTranslation();

    const [exam, setExam] = useState<ExamWithPointsEx>(null!);
    const [savedExam, setSavedExam] = useState<ExamWithPointsEx | null>(null);
    const [saveState, setSaveState] = useState<'saved' | 'saving' | 'error'>('saved');
    const [activeQuestion, setActiveQuestion] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const navigate = useNavigate();

    const { data: webinar, isLoading: webinarIsLoading, error: webinarError } = useQuery(OptionsApi.webinar.get(webinarId!));
    const { data: examData, isLoading: examIsLoading, error: examError } = useQuery(OptionsApi.exam.get(webinarId!, { refetchOnWindowFocus: false }));

    const { mutate: saveExam } = useMutation(OptionsApi.exam.update());

    useEffect(() => {
        if (examData) {
            const version = new Date().getTime();
            setSavedExam({ ...examData, version });
            setExam({ ...examData, version });
        }
    }, [examData]);

    const changeTab = (no: number) => {
        navigate({ hash: tabs[no] });
    };

    const validateExam = (validate: ExamWithPoints): boolean => {
        if (!validate.enabled) {
            setValidationError(null);
            return false;
        }

        if (!validate.title) {
            setValidationError(t('EditExam.Validation.TitleRequired'));
            return false;
        }
        if (!validate.intro) {
            setValidationError(t('EditExam.Validation.IntroRequired'));
            return false;
        }
        if (!validate.showResult && !validate.outro) {
            setValidationError(t('EditExam.Validation.OutroRequired'));
            return false;
        }
        if (validate.showResult && !validate.passed) {
            setValidationError(t('EditExam.Validation.PassedRequired'));
            return false;
        }
        if (validate.showResult && !validate.failed) {
            setValidationError(t('EditExam.Validation.FailedRequired'));
            return false;
        }
        if (validate.questions.length === 0) {
            setValidationError(t('EditExam.Validation.QuestionsRequired'));
            return false;
        }
        let index = 0;
        if ((index = validate.questions.findIndex((q) => !q.questionText)) !== -1) {
            setValidationError(t('EditQuestionnaire.Validation.QuestionRequired', { question: index + 1 }));
            return false;
        }
        if ((index = validate.questions.findIndex((q) => q.questionType !== QuestionType.Open && q.hasOtherOption && !q.otherOptionText)) !== -1) {
            setValidationError(t('EditQuestionnaire.Validation.OtherOptionTextRequired', { question: index + 1 }));
            return false;
        }
        if ((index = validate.questions.findIndex((q) => q.questionType !== QuestionType.Open && q.options.length === 0)) !== -1) {
            setValidationError(t('EditQuestionnaire.Validation.OptionsRequired', { question: index + 1 }));
            return false;
        }
        if (
            (index = validate.questions.findIndex((q) => q.questionType !== QuestionType.Open && q.options.some((o) => !o.text || o.text.trim() === ''))) !== -1
        ) {
            const question = validate.questions[index];
            const optionIndex = question.options.findIndex((o) => !o.text || o.text.trim() === '');
            setValidationError(t('EditQuestionnaire.Validation.OptionTextRequired', { question: index + 1, option: optionIndex + 1 }));
            return false;
        }
        if ((index = validate.questions.findIndex((q) => q.questionType !== QuestionType.Open && q.options.some((o) => !Number.isInteger(o.value)))) !== -1) {
            const question = validate.questions[index];
            const optionIndex = question.options.findIndex((o) => !Number.isInteger(o.value));
            setValidationError(t('EditQuestionnaire.Validation.OptionValueRequired', { question: index + 1, option: optionIndex + 1 }));
            return false;
        }
        setValidationError(null);
        return true;
    };

    useEffect(() => {
        if (!exam || !savedExam) {
            return;
        }

        if (savedExam.version === exam.version) {
            return;
        }

        if (webinarIsLoading || examIsLoading) {
            return;
        }

        const save = { ...exam, isValid: validateExam(exam) };
        setSaveState('saving');
        const handle = setTimeout(() => {
            saveExam(
                { webinarId: webinarId!, exam: save },
                {
                    onSuccess: () => {
                        setSaveState('saved');
                        setSavedExam(save);
                    },
                    onError: () => setSaveState('error')
                }
            );
        }, 1000);
        return () => clearTimeout(handle);
    }, [exam, savedExam, webinarIsLoading, examIsLoading]);

    const error = webinarError || examError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (!webinar || !exam || webinarIsLoading || examIsLoading) {
        return <LoaderPage></LoaderPage>;
    }

    const addQuestion = (type: QuestionType) => {
        const questionId = crypto.randomUUID();
        setActiveQuestion(questionId);
        setExam({
            ...exam,
            questions: [
                ...exam.questions,
                {
                    questionId,
                    questionText: '',
                    questionType: type,
                    hasOtherOption: false,
                    options: [],
                    required: false
                }
            ],
            version: exam.version + 1
        });
    };

    const moveQuestionUp = (index: number) => {
        if (index > 0) {
            const updatedQuestions = [...exam.questions];
            [updatedQuestions[index - 1], updatedQuestions[index]] = [updatedQuestions[index], updatedQuestions[index - 1]];
            setExam({ ...exam, questions: updatedQuestions, version: exam.version + 1 });
        }
    };

    const moveQuestionDown = (index: number) => {
        if (index < exam.questions.length - 1) {
            const updatedQuestions = [...exam.questions];
            [updatedQuestions[index], updatedQuestions[index + 1]] = [updatedQuestions[index + 1], updatedQuestions[index]];
            setExam({ ...exam, questions: updatedQuestions, version: exam.version + 1 });
        }
    };

    return (
        <ContainerNoTopSpace>
            <PageHeader>
                <PageHeaderContent>
                    <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />
                </PageHeaderContent>
            </PageHeader>
            <ContainerWebinarInner>
                <BackToOriginLink fallback="content" />
                <div>
                    {saveState === 'saved' && <p className="text-success mb-4">{t('EditExam.Save.Saved')}</p>}
                    {saveState === 'saving' && <p className="text-orange-500 mb-4">{t('EditExam.Save.Saving')}</p>}
                    {saveState === 'error' && <p className="text-warning mb-4">{t('EditExam.Save.Error')}</p>}
                </div>
                <ContentHeader contentHeader={t('EditExam.Pageheading')} />
                {validationError && (
                    <div className="inline-flex items-center bg-warning-light rounded-md text-sm mb-6 pl-3 pr-4 py-2">
                        <WarningOutlined classNames="w-6 md:w-8 fill-warning -mt-0.5 mr-2 flex-none" /> {validationError}
                    </div>
                )}
                <div className="max-w-4xl">
                    <div className="max-w-lg">
                        <Input
                            classNames="mb-6"
                            label={t('EditExam.Label.Title')}
                            value={exam.title}
                            onChange={(e) => setExam({ ...exam, title: e.target.value, version: exam.version + 1 })}
                        />
                    </div>{' '}
                    <TabGroup direction={TabGroup.direction.HORIZONTAL}>
                        <TabGroup.TabList numTabs={3} activeTab={activeTab} setActiveTab={changeTab}>
                            <TabGroup.Tab activeTab={activeTab} setActiveTab={changeTab} index={0} className="nav-tab" activeClassName="nav-tab--active">
                                <span onClick={() => changeTab(0)}>{t('EditExam.Tab.Settings')}</span>
                            </TabGroup.Tab>
                            <TabGroup.Tab activeTab={activeTab} setActiveTab={changeTab} index={1} className="nav-tab" activeClassName="nav-tab--active">
                                <span onClick={() => changeTab(1)}>{t('EditExam.Tab.Intro')}</span>
                            </TabGroup.Tab>
                            <TabGroup.Tab activeTab={activeTab} setActiveTab={changeTab} index={2} className="nav-tab" activeClassName="nav-tab--active">
                                <span onClick={() => changeTab(2)}>{t('EditExam.Tab.Questions')}</span>
                            </TabGroup.Tab>
                            {!exam.showResult && (
                                <TabGroup.Tab activeTab={activeTab} setActiveTab={changeTab} index={3} className="nav-tab" activeClassName="nav-tab--active">
                                    <span onClick={() => changeTab(3)}>{t('EditExam.Tab.Outro')}</span>
                                </TabGroup.Tab>
                            )}
                            {exam.showResult && (
                                <>
                                    <TabGroup.Tab
                                        activeTab={activeTab}
                                        setActiveTab={changeTab}
                                        index={4}
                                        className="nav-tab"
                                        activeClassName="nav-tab--active"
                                    >
                                        <span onClick={() => changeTab(4)}>{t('EditExam.Tab.Passed')}</span>
                                    </TabGroup.Tab>
                                    <TabGroup.Tab
                                        activeTab={activeTab}
                                        setActiveTab={changeTab}
                                        index={5}
                                        className="nav-tab"
                                        activeClassName="nav-tab--active"
                                    >
                                        <span onClick={() => changeTab(5)}>{t('EditExam.Tab.Failed')}</span>
                                    </TabGroup.Tab>
                                </>
                            )}
                        </TabGroup.TabList>
                        <TabGroup.TabPanel
                            activeTab={activeTab}
                            setActiveTab={changeTab}
                            index={0}
                            className="bg-white rounded-b-lg transition-all transform"
                            activeClassName="opacity-100 duration-500 translate-x-0"
                            inactiveClassName="absolute left-0 right-0 top-12 opacity-0 -translate-x-2 h-0 overflow-hidden"
                        >
                            <div className="p-4 md:p-8">
                                <Heading variant="h2" classNames="mb-6">
                                    {t('EditExam.Heading.Settings')}
                                </Heading>

                                <div className="pl-10 mb-2">
                                    <input
                                        type="checkbox"
                                        role="switch"
                                        id="enabled"
                                        checked={exam.enabled}
                                        onChange={() => setExam({ ...exam, enabled: !exam.enabled, version: exam.version + 1 })}
                                        className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                                    />
                                    <label htmlFor="enabled" className="inline-block">
                                        {t('EditExam.Label.Enable')}
                                    </label>
                                </div>
                                <div className="pl-10 mb-2">
                                    <input
                                        type="checkbox"
                                        role="switch"
                                        id="required"
                                        checked={exam.required}
                                        onChange={() => setExam({ ...exam, required: !exam.required, version: exam.version + 1 })}
                                        className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                                    />
                                    <label htmlFor="required" className="inline-block">
                                        {t('EditExam.Label.Required')}
                                    </label>
                                </div>
                                <div className="pl-10 mb-2">
                                    <input
                                        type="checkbox"
                                        role="switch"
                                        id="showResult"
                                        checked={exam.showResult}
                                        onChange={() => setExam({ ...exam, showResult: !exam.showResult, version: exam.version + 1 })}
                                        className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                                    />
                                    <label htmlFor="showResult" className="inline-block">
                                        {t('EditExam.Label.ShowResult')}
                                    </label>
                                </div>
                                {exam.showResult && (
                                    <>
                                        <div className="max-w-lg mb-2">
                                            <Input
                                                classNames="mb-6"
                                                label={t('EditExam.Label.PassingScore')}
                                                type="number"
                                                min={0}
                                                step={1}
                                                value={exam.passingScore}
                                                onChange={(e) => setExam({ ...exam, passingScore: parseInt(e.target.value), version: exam.version + 1 })}
                                            />
                                        </div>
                                        <div className="pl-10 mb-2">
                                            <input
                                                type="checkbox"
                                                role="switch"
                                                id="showWrongAnswers"
                                                checked={exam.showWrongAnswers}
                                                onChange={() => setExam({ ...exam, showWrongAnswers: !exam.showWrongAnswers, version: exam.version + 1 })}
                                                className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                                            />
                                            <label htmlFor="showWrongAnswers" className="inline-block">
                                                {t('EditExam.Label.ShowWrongAnswers')}
                                            </label>
                                        </div>
                                        <div className={`pl-10 ${exam.allowRetries ? 'mb-2' : 'mb-10'}`}>
                                            <input
                                                type="checkbox"
                                                role="switch"
                                                id="allowRetries"
                                                checked={exam.allowRetries}
                                                onChange={() => setExam({ ...exam, allowRetries: !exam.allowRetries, version: exam.version + 1 })}
                                                className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                                            />
                                            <label htmlFor="allowRetries" className="inline-block">
                                                {t('EditExam.Label.AllowRetries')}
                                            </label>
                                        </div>
                                        {exam.allowRetries && (
                                            <div className="max-w-lg mb-10">
                                                <span className="block mb-4">{t('EditExam.Info.ZeroMeansUnlimitedRetries')}</span>
                                                {/** Misschien andere manier van retries ophogen/verlagen en dan 0 tonen als "Unlimited" */}
                                                <Input
                                                    classNames="mb-6"
                                                    label={t('EditExam.Label.MaximumRetries')}
                                                    type="number"
                                                    min={0}
                                                    step={1}
                                                    value={exam.maximumRetries}
                                                    onChange={(e) => setExam({ ...exam, maximumRetries: parseInt(e.target.value), version: exam.version + 1 })}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </TabGroup.TabPanel>
                        <TabGroup.TabPanel
                            activeTab={activeTab}
                            setActiveTab={changeTab}
                            index={1}
                            className="bg-white rounded-b-lg transition-all transform"
                            activeClassName="opacity-100 duration-500 translate-x-0"
                            inactiveClassName="absolute left-0 right-0 top-12 opacity-0 -translate-x-2 h-0 overflow-hidden"
                        >
                            <div className="p-4 md:p-8">
                                <Heading variant="h2" classNames="mb-6">
                                    {t('EditExam.Heading.Intro')}
                                </Heading>
                                <TextArea
                                    label={t('EditExam.Label.Intro')}
                                    value={exam.intro}
                                    onChange={(e) => setExam({ ...exam, intro: e.target.value, version: exam.version + 1 })}
                                    classNames="mb-0"
                                    textAreaClassNames="field-sizing-content"
                                />
                            </div>
                        </TabGroup.TabPanel>
                        <TabGroup.TabPanel
                            activeTab={activeTab}
                            setActiveTab={changeTab}
                            index={2}
                            className="bg-white rounded-b-lg transition-all transform"
                            activeClassName="opacity-100 duration-500 translate-x-0"
                            inactiveClassName="absolute left-0 right-0 top-12 opacity-0 -translate-x-2 h-0 overflow-hidden"
                        >
                            <div className="p-4 md:p-8">
                                <Heading variant="h2" classNames="mb-6">
                                    {t('EditExam.Heading.Questions')}
                                </Heading>
                                {exam.questions.length === 0 && <div className="text-gray-500">{t('EditExam.NoQuestions')}</div>}
                                {exam.questions.map((question, index) => (
                                    <div className="w-full max-w-2xl flex mb-4" key={question.questionId}>
                                        <div className="flex flex-col -mr-px z-10">
                                            <div className="size-12 bg-white border-y border-l border-gray-200 rounded-l-lg rounded-br-lg inline-flex items-center justify-center font-medium">
                                                {`${index + 1}.`}
                                            </div>
                                            <Button disabled={index === 0} iconbtn onClick={() => moveQuestionUp(index)}>
                                                <SortArrow classNames="size-6 rotate-180 stroke-black" />
                                                <span className="sr-only">{t('EditQuestionnaire.Button.QuestionSortUp')}</span>
                                            </Button>
                                            <Button disabled={index === exam.questions.length - 1} iconbtn onClick={() => moveQuestionDown(index)}>
                                                <SortArrow classNames="size-6 stroke-black" />
                                                <span className="sr-only">{t('EditQuestionnaire.Button.QuestionSortDown')}</span>
                                            </Button>
                                        </div>
                                        <div className="flex-1 border border-gray-200 rounded-b-lg rounded-tr-lg p-4 menu:p-6">
                                            <div className="flex justify-end gap-4 xs:float-right ml-2 mb-2">
                                                <Button
                                                    data-testid={`exam-question-edit-button-${index}`}
                                                    variant="primary-outline"
                                                    iconbtn
                                                    onClick={() => setActiveQuestion(activeQuestion === question.questionId ? null : question.questionId)}
                                                >
                                                    <Edit classNames="size-6 stroke-current" />
                                                    <span className="sr-only">{t('EditQuestionnaire.Button.EditQuestion')}</span>
                                                </Button>
                                                <Button
                                                    data-testid={`exam-question-delete-button-${index}`}
                                                    variant="warning-outline"
                                                    iconbtn
                                                    onClick={() =>
                                                        setExam({
                                                            ...exam,
                                                            questions: exam.questions.filter((q) => q.questionId !== question.questionId),
                                                            version: exam.version + 1
                                                        })
                                                    }
                                                >
                                                    <Delete classNames="size-6 stroke-current" />
                                                    <span className="sr-only">{t('EditQuestionnaire.Button.DeleteQuestion')}</span>
                                                </Button>
                                            </div>
                                            {activeQuestion === question.questionId && (
                                                <EditQuestion
                                                    question={question}
                                                    updateQuestion={(updated) =>
                                                        setExam({
                                                            ...exam,
                                                            questions: exam.questions.map((q) => (q.questionId === question.questionId ? updated : q)),
                                                            version: exam.version + 1
                                                        })
                                                    }
                                                />
                                            )}
                                            {activeQuestion !== question.questionId && (
                                                <table className="inline">
                                                    <tbody>
                                                        <tr>
                                                            <td className="py-1 pr-2">{t('EditQuestionnaire.QuestionDetail.Question')}</td>
                                                            <td className="py-1 font-medium">{question.questionText}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="py-1 pr-2">{t('EditQuestionnaire.QuestionDetail.Options')}</td>
                                                            <td className="py-1 font-medium">
                                                                {!question.hasOtherOption
                                                                    ? question.options.length
                                                                    : `${question.options.length + 1}${t('EditQuestionnaire.QuestionDetail.OtherOptionText')}`}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td className="py-1 pr-2">{t('EditQuestionnaire.QuestionDetail.Required')}</td>
                                                            <td className="py-1 font-medium">{question.required ? 'Yes' : 'No'}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <div className="flex flex-wrap gap-4 mt-10">
                                    <Button variant="primary" onClick={() => addQuestion(QuestionType.MultipleChoice)}>
                                        <Plus classNames="size-5 stroke-current -ms-1 me-1" />
                                        <span>{t('EditQuestionnaire.Button.AddMultipleChoiceQuestion')}</span>
                                    </Button>
                                    <Button
                                        data-testid="exam-add-single-choice-question-button"
                                        variant="primary"
                                        onClick={() => addQuestion(QuestionType.SingleChoice)}
                                    >
                                        <Plus classNames="size-5 stroke-current -ms-1 me-1" />
                                        <span>{t('EditQuestionnaire.Button.AddSingleChoiceQuestion')}</span>
                                    </Button>
                                    <Button variant="primary" onClick={() => addQuestion(QuestionType.Open)}>
                                        <Plus classNames="size-5 stroke-current -ms-1 me-1" />
                                        <span>{t('EditQuestionnaire.Button.AddOpenQuestion')}</span>
                                    </Button>
                                </div>
                            </div>
                        </TabGroup.TabPanel>
                        <TabGroup.TabPanel
                            activeTab={activeTab}
                            setActiveTab={changeTab}
                            index={3}
                            className="bg-white rounded-b-lg transition-all transform"
                            activeClassName="opacity-100 duration-500 translate-x-0"
                            inactiveClassName="absolute left-0 right-0 top-12 opacity-0 -translate-x-2 h-0 overflow-hidden"
                        >
                            <div className="p-4 md:p-8">
                                <Heading variant="h2" classNames="mb-6">
                                    {t('EditExam.Heading.Outro')}
                                </Heading>
                                <div className="max-w-lg">
                                    <TextArea
                                        label={t('EditExam.Label.Outro')}
                                        value={exam.outro}
                                        onChange={(e) => setExam({ ...exam, outro: e.target.value, version: exam.version + 1 })}
                                        classNames="mb-0"
                                        textAreaClassNames="field-sizing-content"
                                    />
                                </div>
                            </div>
                        </TabGroup.TabPanel>
                        <TabGroup.TabPanel
                            activeTab={activeTab}
                            setActiveTab={changeTab}
                            index={4}
                            className="bg-white rounded-b-lg transition-all transform"
                            activeClassName="opacity-100 duration-500 translate-x-0"
                            inactiveClassName="absolute left-0 right-0 top-12 opacity-0 -translate-x-2 h-0 overflow-hidden"
                        >
                            <div className="p-4 md:p-8">
                                <Heading variant="h2" classNames="mb-6">
                                    {t('EditExam.Heading.Passed')}
                                </Heading>
                                <div className="max-w-lg">
                                    <TextArea
                                        label={t('EditExam.Label.Passed')}
                                        value={exam.passed}
                                        onChange={(e) => setExam({ ...exam, passed: e.target.value, version: exam.version + 1 })}
                                        classNames="mb-0"
                                        textAreaClassNames="field-sizing-content"
                                    />
                                </div>
                            </div>
                        </TabGroup.TabPanel>
                        <TabGroup.TabPanel
                            activeTab={activeTab}
                            setActiveTab={changeTab}
                            index={5}
                            className="bg-white rounded-b-lg transition-all transform"
                            activeClassName="opacity-100 duration-500 translate-x-0"
                            inactiveClassName="absolute left-0 right-0 top-12 opacity-0 -translate-x-2 h-0 overflow-hidden"
                        >
                            <div className="p-4 md:p-8">
                                <Heading variant="h2" classNames="mb-6">
                                    {t('EditExam.Heading.Failed')}
                                </Heading>
                                <div className="max-w-lg">
                                    <TextArea
                                        label={t('EditExam.Label.Failed')}
                                        value={exam.failed}
                                        onChange={(e) => setExam({ ...exam, failed: e.target.value, version: exam.version + 1 })}
                                        classNames="mb-0"
                                        textAreaClassNames="field-sizing-content"
                                    />
                                </div>
                            </div>
                        </TabGroup.TabPanel>
                    </TabGroup>
                </div>
            </ContainerWebinarInner>
        </ContainerNoTopSpace>
    );
};
